import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { HiArrowLeft, HiPencil, HiSave, HiTemplate, HiTrash, HiX } from 'react-icons/hi'
import { Button, Spinner } from 'flowbite-react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { API_BASE } from '../constants/inviteCode'
import AuthorityForm, { toFormState, toPayload } from '../components/AuthorityForm'
import AuthorityBoundaryMap from '../components/AuthorityBoundaryMap'
import RelatedLeadsTable from '../components/RelatedLeadsTable'
import { parseBoundaryGeoJson } from '../utils/boundary'
import { toastApiError } from '../utils/apiError'
import { deleteAuthorityWithLeads } from '../utils/authorityDelete'

const CityAdminDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { token } = useAuth()
  const [authority, setAuthority] = useState(null)
  const [formData, setFormData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [isEditing, setIsEditing] = useState(true)

  const boundaryGeoJson = useMemo(
    () => parseBoundaryGeoJson(authority),
    [authority]
  )

  const fetchAuthority = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${API_BASE}/authorities/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setAuthority(response.data)
      setFormData(toFormState(response.data))
    } catch (error) {
      toastApiError(error, 'Failed to load city')
      navigate('/city-admin')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAuthority()
    // fetchAuthority reads only id/token, both listed. It is recreated every
    // render, so listing it would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token])

  const handleCancelEdit = () => {
    setFormData(toFormState(authority))
    setIsEditing(false)
  }

  const handleSave = async () => {
    if (!formData?.name?.trim()) {
      toast.error('City name is required')
      return
    }

    setSaving(true)
    try {
      const response = await axios.put(
        `${API_BASE}/authorities/${id}`,
        toPayload(formData),
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setAuthority(response.data)
      setFormData(toFormState(response.data))
      setIsEditing(false)
      toast.success('City updated')
    } catch (error) {
      toastApiError(error, 'Failed to update city')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      const domain = formData?.official_domain || authority.official_domain
      const { deleted, cancelled } = await deleteAuthorityWithLeads({
        token,
        authorityId: id,
        domain,
        name: authority?.name,
      })
      if (cancelled) {
        setDeleting(false)
        return
      }
      if (deleted) {
        toast.success('City deleted')
        navigate('/city-admin')
      }
    } catch (error) {
      toastApiError(error, 'Failed to delete city')
      setDeleting(false)
    }
  }

  if (loading || !formData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link
              to="/city-admin"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-3"
            >
              <HiArrowLeft className="h-4 w-4 mr-1" />
              Back to City Admin
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {authority.name}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              {isEditing
                ? 'Edit this city profile and save your changes.'
                : 'View this city profile or switch to edit mode.'}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button color="gray" onClick={() => navigate(`/departments?authority=${id}`)}>
              <HiTemplate className="h-4 w-4 mr-2" />
              Departments
            </Button>
            {isEditing ? (
              <>
                <Button color="gray" onClick={handleCancelEdit} disabled={saving}>
                  <HiX className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <HiSave className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button color="gray" onClick={() => setIsEditing(true)}>
                  <HiPencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button color="failure" onClick={handleDelete} disabled={deleting}>
                  {deleting ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <HiTrash className="h-4 w-4 mr-2" />
                  )}
                  Delete City
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <AuthorityForm
          authority={authority}
          isEditing={isEditing}
          formData={formData}
          onChange={setFormData}
          token={token}
          mapAside={
            boundaryGeoJson ? (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  City limits
                </h2>
                <AuthorityBoundaryMap boundaryGeoJson={boundaryGeoJson} />
              </div>
            ) : null
          }
        />

        {isEditing && (
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-wrap">
            <Button color="gray" onClick={handleCancelEdit} disabled={saving}>
              <HiX className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                <HiSave className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Related Leads
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Leads associated with this city&apos;s official domain.
          </p>
        </div>
        <RelatedLeadsTable
          token={token}
          domain={formData?.official_domain || authority.official_domain}
        />
      </div>
    </div>
  )
}

export default CityAdminDetail
